import { Divider, Form } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import { DefaultRouteTabs } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import FinancialBalanceteForm from '@/components/Reports/FinancialReports/FinancialBalanceteForm';
import { useReportGenerate } from '@/hooks';
import { useFinancialTrialBalance } from '@/core/domain/NetReport/Financial/Financial.hooks';
import useFileLoading, { FILE_FORMAT, FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import {
  FinancialTrialBalance,
  FinancialTrialBalanceForm,
} from '@/core/domain/NetReport/Financial/Financial.types';
import { DateTimeFormat } from '@/utils/moment';

export default function FinancialBalancetePage({ route }: PageProps) {
  const [form] = Form.useForm<FinancialTrialBalanceForm>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useFinancialTrialBalance();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const { situation, initialDate, finalDate, companiesIds, ...values } =
        await form.validateFields();

      const obj: FinancialTrialBalance = {
        ...values,
        reportContentType: FILE_FORMAT[format],
        isOpen: situation.includes('OPEN'),
        isSettled: situation.includes('LIQUIDATED'),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
        showPayablesAndReceivables: values.showPayablesAndReceivables
          ? true
          : false,
        showBankEntries: values.showBankEntries ? true : false,
        tariffs: values.tariffs ? true : false,
        companiesIds: companiesIds ? companiesIds.toString() : '',
        planAccountIds: values.planAccountIds
          ? values?.planAccountIds.toString()
          : '',
      };

      const result = await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_balancete.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_balancete.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_balancete.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={useIntl().formatMessage({ id: route.name })} />
      <FinancialBalanceteForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}
